import React, { useLayoutEffect, useRef, useState } from 'react';
import './HeroImage.css';
import { UnsplashCredit } from './UnsplashCredit';

type HeroImageSize = 'thumb-sm' | 'thumb-lg' | 'hero';

interface Attribution {
  photographer: string;
  link: string;
}

interface HeroImageProps {
  src?: string;
  fallbackKey: string;
  size?: HeroImageSize;
  className?: string;
  attribution?: Attribution;
  /**
   * Told whether the fallback (`hero-image--bare`) is what is on screen:
   * on mount, and again whenever that changes, including after the image
   * fails to load. A caller that paints a scrim or white text over the image
   * needs it, because on paper those assumed a photograph and read pale
   * (decision 25, r1123 finding 1). Callers that paint nothing over the
   * image omit it.
   */
  onBareChange?: (bare: boolean) => void;
  /**
   * Whether the bare `hero` fallback draws `fallbackKey` as its ink label.
   * Default true: where the label is the only text, it is decision 25's ink
   * label. A caller that draws its own title over the image passes false, or
   * the title shows twice (or the two ink strings overlap) on paper.
   */
  showLabel?: boolean;
}

// Uses CSS vars so it follows the paper tokens (matches ChatOverlay header)

const HeroImage: React.FC<HeroImageProps> = ({
  src,
  fallbackKey,
  size = 'thumb-sm',
  className = '',
  attribution,
  onBareChange,
  showLabel = true,
}) => {
  // The source that failed to load, not a lifetime flag: a later, different
  // `src` on the same instance (the next day's photo, a pool rotation) gets a
  // fresh <img>, while the same failed `src` stays on paper (r1126 finding 1).
  const [failedSrc, setFailedSrc] = useState<string | undefined>(undefined);
  const failed = !!src && failedSrc === src;
  const shouldShowImage = src && !failed;
  const bare = !shouldShowImage;

  // The latest callback, so an inline arrow does not re-fire the effect.
  const notifyRef = useRef(onBareChange);
  notifyRef.current = onBareChange;
  // A layout effect, not a passive one: it runs before the browser paints,
  // so the caller's scrim and text change in the same frame as the image.
  useLayoutEffect(() => {
    notifyRef.current?.(bare);
  }, [bare]);

  const ariaLabel = attribution
    ? `Photo by ${attribution.photographer}`
    : undefined;

  return (
    <div
      className={`hero-image hero-image--${size}${bare ? ' hero-image--bare' : ''} ${className}`}
      aria-label={size !== 'hero' ? ariaLabel : undefined}
    >
      {shouldShowImage ? (
        <img
          key={src}
          src={src}
          alt={fallbackKey}
          className="hero-image__img"
          onError={() => setFailedSrc(src)}
          loading={size === 'hero' ? 'eager' : 'lazy'}
          decoding="async"
          {...(size === 'hero' ? { fetchPriority: 'high' } as Record<string, string> : {})}
        />
      ) : (
        size === 'hero' && showLabel && (
          <span className="hero-image__label">{fallbackKey}</span>
        )
      )}
      {shouldShowImage && size === 'hero' && attribution && (
        <UnsplashCredit
          photographer={attribution.photographer}
          profileLink={attribution.link}
          className="hero-image__attribution"
        />
      )}
    </div>
  );
};

export default HeroImage;
